"use client";

import { PortableText } from "@portabletext/react";
import { richTextComponent } from "@/sanity/sanityPortableText";
import Image from "next/image";
import type { PageAffiliateSchema } from "@/sanity/pages/PageAffiliateTemplate";
import { motion } from "framer-motion";
import { Fragment, useState } from "react";

import ribbonHighlight from "@/public/global/ribbon-highlight.jpg";
import arrow from "@/public/global/arrow.svg";
import dealsBG from "@/public/affiliate-template/deals-bg.png";
import dealsDotBG from "@/public/affiliate-template/deals-dot-bg.png";

type TextFieldType = "text1" | "text2" | "text3" | "text4" | "text5";

export default function Deals({ data }: { data: PageAffiliateSchema }) {
  const [selectedTextField, setSelectedTextField] = useState<{
    [key: number]: TextFieldType;
  }>(() => {
    // Initialize with text1 selected for all items by default
    const initialState: { [key: number]: TextFieldType } = {};
    data?.dealsItems?.forEach((_, index) => {
      initialState[index] = "text1";
    });
    return initialState;
  });
  return (
    <section className="w-full lg:px-0 px-[2rem] pt-[6rem] lg:pt-[17.5rem]">
      <div className="w-[min(176.75rem,100%)] mx-auto  rounded-[1.5rem] lg:rounded-[5rem]">
        <div className="relative mx-auto w-[min(178.5rem,100%)]">
          <motion.div
            initial={{ opacity: 0, y: -40, filter: "blur(10px)" }}
            whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{
              duration: 0.3,
              ease: [0.25, 0.4, 0.25, 1],
            }}>
            {/* Title */}
            {data?.dealsTitle && (
              <div className="mb-[5rem] richtext richtext-start lg:richtext-center prose-h6:text-black prose-h6:text-[2rem] prose-h6:font-blacker prose-h6:italic prose-h6:mb-[2rem] prose-headings:text-black">
                <PortableText
                  value={data?.dealsTitle}
                  components={richTextComponent}
                />
              </div>
            )}
          </motion.div>

          {/* Items */}
          {data?.dealsItems && data.dealsItems.length > 0 && (
            <div className="flex flex-col gap-[2.5rem] lg:gap-[1.5rem]">
              {data.dealsItems.map((item, index) => (
                <motion.div
                  key={index}
                  className="flex relative min-h-[45rem] overflow-clip lg:p-[1rem]"
                  initial={{ opacity: 0, x: -50, filter: "blur(10px)" }}
                  whileInView={{ opacity: 1, x: 0, filter: "blur(0px)" }}
                  viewport={{ once: true, margin: "-100px" }}
                  transition={{
                    duration: 0.3,
                    delay: index * 0.1,
                    ease: [0.25, 0.4, 0.25, 1],
                  }}>
                  <div
                    className={`flex relative flex-col items-center w-full border lg:flex-row lg:py-[2rem] pt-[2rem] px-[2rem] rounded-[0.75rem] border-black/5 lg:gap-[5rem] gap-[3rem] min-h-[45rem] lg:pr-[5rem] ${index === 0 ? "bg-[#F4F4F4]" : "bg-[#FFFFFF]"}`}>
                    <div
                      className={`flex flex-col justify-between p-[2rem] lg:p-[3rem] rounded-[.5rem] flex-shrink-0 w-[min(52.375rem,100%)] lg:self-stretch relative overflow-clip`}>
                      {index === 0 ? (
                        <Image
                          src={dealsBG}
                          alt=""
                          className="object-cover absolute inset-0 w-full h-full z-[1]"
                        />
                      ) : (
                        <Image
                          src={dealsDotBG}
                          alt=""
                          className="object-cover absolute inset-0 w-full h-full z-[1]"
                        />
                      )}
                      <div className="flex relative flex-col justify-between h-full z-[5]">
                        <div className="relative z-[5]">
                          {/* Chip */}
                          {item.chip && (
                            <div className="relative mb-[2rem]">
                              <div
                                className={`rounded-full py-[1.125rem] px-[1.5rem] w-fit ${index === 0 ? "bg-white/20" : "bg-[#103037]"}`}>
                                <p className={`text-white text-[1.75rem] leading-[100%] font-[500] m-0 uppercase`}>
                                  {item.chip}
                                </p>
                              </div>
                            </div>
                          )}

                          {/* Title */}
                          {item.title && (
                            <h3
                              className={` text-[4.375rem] leading-[120%] font-[500]  ${index === 0 ? "text-white" : "text-[#103037]"}`}
                              style={{ filter: "url(#innerShadow)" }}>
                              {item.title}
                            </h3>
                          )}
                        </div>

                        <div className="">
                          {/* Tags */}
                          {item.tagsTitle && (
                            <p
                              className={` text-[1.75rem] mb-[2rem] leading-[135%] font-[500] font-geist opacity-60 ${index === 0 ? "text-white" : "text-[#103037]"}`}>
                              {item.tagsTitle}
                            </p>
                          )}

                          {item.tags && item.tags.length > 0 && (
                            <div className="flex flex-wrap gap-[1rem]">
                              {item.tags.map((tag, tagIndex) => (
                                <div
                                  key={tagIndex}
                                  className={`rounded-full backdrop-blur-[2rem] py-[0.5rem] px-[1.5rem] ${index === 0 ? "bg-white/[15%]" : "bg-[#103037]/[15%]"}`}>
                                  <p
                                    className={`font-geist text-[2rem] m-0 leading-[125%] font-[400] ${index === 0 ? "text-white" : "text-[#103037]"}`}>
                                    {tag.text}
                                  </p>
                                </div>
                              ))}
                            </div>
                          )}
                          {item?.callout && (
                            <div className="w-full mt-[3rem]">
                              <p
                                className={`${index === 0 ? "text-white" : "text-[#103037]"} font-[500] text-[2.25rem] m-0  font-sf leading-[134%] tracking-[-0.0675rem]`}>
                                {item?.callout}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Table */}
                    <div className="flex flex-col flex-1 justify-center w-full lg:py-[2rem]">
                      {item.table && item.table.tableItem && item.table.tableItem.length > 0 && (
                        <Fragment>
                          <table className="hidden relative w-full border-collapse lg:block">
                            {item?.showSideBanner && (
                              <div className="hidden absolute pointer-events-none lg:block -left-[3rem] -right-[1.5rem] top-[5.5rem] h-[9.75rem] bg-[#D9D9D9] rounded-[0.5rem]">
                                <Image
                                  className="absolute bottom-0 translate-y-[2.5rem] -right-[2.5rem] !w-[5rem]"
                                  src={arrow}
                                  alt="arrow"
                                />
                              </div>
                            )}

                            <tbody className="flex relative justify-between h-full">
                              {item.table.tableItem.map((column, columnIndex) => {
                                const isLast = columnIndex === item.table.tableItem.length - 1;
                                const textSize = isLast ? "prose-p:text-[1.75rem]" : "prose-p:text-[2rem]";
                                return (
                                  <tr
                                    key={columnIndex}
                                    className="flex flex-1 h-full">
                                    {/* Column Header */}
                                    <td className="w-full h-full align-top">
                                      <div className="border-b border-black/10 pb-[1.75rem]">
                                        <p
                                          className="font-sf text-[#111820]/50 text-[1.5rem] leading-[120%] uppercase"
                                          dangerouslySetInnerHTML={{
                                            __html: column.title,
                                          }}
                                        />
                                      </div>
                                      {/* Text1 */}
                                      <div
                                        className={`richtext richtext-start prose-strong:text-[#111820] prose-p:text-[#111820]/80 prose-p:font-sf ${textSize} prose-p:leading-[135%] prose-em:not-italic prose-em:line-through prose-strong:font-[500] h-[5rem] mt-[2rem] justify-center flex flex-col`}>
                                        <PortableText
                                          value={column.text1 ?? []}
                                          components={richTextComponent}
                                        />
                                      </div>
                                      <hr className="w-full border-t border-black/10 my-[2.5rem]" />
                                      {/* Text2 */}
                                      <div
                                        className={`richtext richtext-start prose-strong:text-[#111820] prose-p:text-[#111820]/80 prose-p:font-sf ${textSize} prose-p:leading-[135%] prose-em:not-italic prose-em:line-through prose-strong:font-[500] h-[5rem] justify-center flex flex-col`}>
                                        <PortableText
                                          value={column.text2 ?? []}
                                          components={richTextComponent}
                                        />
                                      </div>
                                      <hr className="w-full border-t border-black/10 my-[2.5rem]" />
                                      {/* Text3 */}
                                      <div
                                        className={`richtext richtext-start prose-strong:text-[#111820] prose-p:text-[#111820]/80 prose-p:font-sf ${textSize} prose-p:leading-[135%] prose-em:not-italic prose-em:line-through prose-strong:font-[500] h-[5rem] justify-center flex flex-col`}>
                                        <PortableText
                                          value={column.text3 ?? []}
                                          components={richTextComponent}
                                        />
                                      </div>
                                      {/* Text4 */}
                                      {column.text4 && (
                                        <Fragment>
                                          <hr className="w-full border-t border-black/10 my-[2.5rem]" />
                                          <div
                                            className={`richtext richtext-start prose-strong:text-[#111820] prose-p:text-[#111820]/80 prose-p:font-sf ${textSize} prose-p:leading-[135%] prose-em:not-italic prose-em:line-through prose-strong:font-[500] h-[5rem] justify-center flex flex-col`}>
                                            <PortableText
                                              value={column.text4 ?? []}
                                              components={richTextComponent}
                                            />
                                          </div>
                                        </Fragment>
                                      )}

                                      {column.text5 && (
                                        <Fragment>
                                          <hr className="w-full border-t border-black/10 my-[2.5rem]" />
                                          <div
                                            className={`richtext richtext-start prose-strong:text-[#111820] prose-p:text-[#111820]/80 prose-p:font-sf ${textSize} prose-p:leading-[135%] prose-em:not-italic prose-em:line-through prose-strong:font-[500] h-[5rem] justify-center flex flex-col`}>
                                            <PortableText
                                              value={column.text5 ?? []}
                                              components={richTextComponent}
                                            />
                                          </div>
                                        </Fragment>
                                      )}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>

                          <div className="w-full lg:hidden space-y-[4rem]">
                            {/* First row - horizontal layout with clickable text fields */}
                            {item.table.tableItem[0] && (
                              <div className="mb-[2rem]">
                                {/* Title */}
                                {item.table.tableItem[0].title && (
                                  <p
                                    className={`px-[1rem] font-sf text-[1.5rem] leading-[120%] uppercase [&_br]:hidden [&_br]:after:content-['_'] [&_br]:after:inline-block m-0 mb-[1rem] text-[#111820]/50`}
                                    dangerouslySetInnerHTML={{
                                      __html: item.table.tableItem[0].title.replace(/<br\s*\/?>/gi, " "),
                                    }}
                                  />
                                )}
                                {/* Text fields in a row */}
                                <div className="w-full max-w-full overflow-x-auto rounded-full border border-[#435269]/[15%] [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
                                  <div className="flex w-max gap-[.25rem] p-[.5rem]">
                                    {/* Text1 */}
                                    <button
                                      onClick={() =>
                                        setSelectedTextField((prev) => ({
                                          ...prev,
                                          [index]: "text1",
                                        }))
                                      }
                                      className={`py-[1rem] px-[1.25rem] w-fit rounded-full transition-all ${
                                        selectedTextField[index] === "text1" ? "bg-[#103037]" : ""
                                      }`}>
                                      <div
                                        className={`richtext richtext-start prose-p:font-sf prose-p:text-[2rem] prose-p:leading-[135%] prose-em:not-italic prose-em:line-through prose-strong:font-[500] ${
                                          selectedTextField[index] === "text1"
                                            ? "prose-p:text-[#FFFFFF]"
                                            : "prose-p:text-[#111820]"
                                        }`}>
                                        <PortableText
                                          value={item.table.tableItem[0].text1 ?? []}
                                          components={richTextComponent}
                                        />
                                      </div>
                                    </button>
                                    {/* Text2 */}
                                    <button
                                      onClick={() =>
                                        setSelectedTextField((prev) => ({
                                          ...prev,
                                          [index]: "text2",
                                        }))
                                      }
                                      className={`py-[1rem] px-[1.25rem] w-fit rounded-full transition-all ${
                                        selectedTextField[index] === "text2" ? "bg-[#103037]" : ""
                                      }`}>
                                      <div
                                        className={`richtext richtext-start prose-p:font-sf prose-p:text-[2rem] prose-p:leading-[135%] prose-em:not-italic prose-em:line-through prose-strong:font-[500] ${
                                          selectedTextField[index] === "text2"
                                            ? "prose-p:text-[#FFFFFF]"
                                            : "prose-p:text-[#111820]"
                                        }`}>
                                        <PortableText
                                          value={item.table.tableItem[0].text2 ?? []}
                                          components={richTextComponent}
                                        />
                                      </div>
                                    </button>
                                    {/* Text3 */}
                                    <button
                                      onClick={() =>
                                        setSelectedTextField((prev) => ({
                                          ...prev,
                                          [index]: "text3",
                                        }))
                                      }
                                      className={`py-[1rem] px-[1.25rem] w-fit rounded-full transition-all ${
                                        selectedTextField[index] === "text3" ? "bg-[#103037]" : ""
                                      }`}>
                                      <div
                                        className={`richtext richtext-start prose-p:font-sf prose-p:text-[2rem] prose-p:leading-[135%] prose-em:not-italic prose-em:line-through prose-strong:font-[500] ${
                                          selectedTextField[index] === "text3"
                                            ? "prose-p:text-[#FFFFFF]"
                                            : "prose-p:text-[#111820]"
                                        }`}>
                                        <PortableText
                                          value={item.table.tableItem[0].text3 ?? []}
                                          components={richTextComponent}
                                        />
                                      </div>
                                    </button>
                                    {/* Text4 */}
                                    {item.table.tableItem[0].text4 && (
                                      <button
                                        onClick={() =>
                                          setSelectedTextField((prev) => ({
                                            ...prev,
                                            [index]: "text4",
                                          }))
                                        }
                                        className={`py-[1rem] px-[1.25rem] w-fit rounded-full transition-all ${
                                          selectedTextField[index] === "text4" ? "bg-[#103037]" : ""
                                        }`}>
                                        <div
                                          className={`richtext richtext-start prose-p:font-sf prose-p:text-[2rem] prose-p:leading-[135%] prose-em:not-italic prose-em:line-through prose-strong:font-[500] ${
                                            selectedTextField[index] === "text4"
                                              ? "prose-p:text-[#FFFFFF]"
                                              : "prose-p:text-[#111820]"
                                          }`}>
                                          <PortableText
                                            value={item.table.tableItem[0].text4 ?? []}
                                            components={richTextComponent}
                                          />
                                        </div>
                                      </button>
                                    )}
                                    {/* Text5 */}
                                    {item.table.tableItem[0].text5 && (
                                      <button
                                        onClick={() =>
                                          setSelectedTextField((prev) => ({
                                            ...prev,
                                            [index]: "text5",
                                          }))
                                        }
                                        className={`py-[1rem] px-[1.25rem] w-fit rounded-full transition-all ${
                                          selectedTextField[index] === "text5" ? "bg-[#103037]" : ""
                                        }`}>
                                        <div
                                          className={`richtext richtext-start prose-p:font-sf prose-p:text-[2rem] prose-p:leading-[135%] prose-em:not-italic prose-em:line-through prose-strong:font-[500] ${
                                            selectedTextField[index] === "text5"
                                              ? "prose-p:text-[#FFFFFF]"
                                              : "prose-p:text-[#111820]"
                                          }`}>
                                          <PortableText
                                            value={item.table.tableItem[0].text5 ?? []}
                                            components={richTextComponent}
                                          />
                                        </div>
                                      </button>
                                    )}
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* Remaining rows - vertical layout showing only selected field */}
                            <div className="px-[2rem]">
                              {selectedTextField[index] &&
                                item.table.tableItem.slice(1).map((column, columnIndex) => (
                                  <div
                                    key={columnIndex}
                                    className="flex items-start border-b last:border-b-0 gap-[2rem] mb-[2rem] pb-[2rem] border-black/10">
                                    {/* Title */}
                                    <div className="flex-1">
                                      <p
                                        className={`font-sf text-[2rem] leading-[120%] uppercase m-0 ${index === 0 ? "text-[#111820]/50" : "text-[#163E03]/50"}`}
                                        dangerouslySetInnerHTML={{
                                          __html: column.title,
                                        }}
                                      />
                                    </div>
                                    {/* Selected text field */}
                                    <div className="flex-1">
                                      <div
                                        className={`richtext richtext-start prose-p:font-sf prose-p:text-[2rem] prose-p:leading-[135%] prose-em:not-italic prose-em:line-through prose-strong:font-[500] prose-p:m-0 ${index === 0 ? "prose-p:text-[#111820]/80 prose-strong:text-[#111820]" : "prose-p:text-[#163E03]/80 prose-strong:text-[#163E03]"}`}>
                                        <PortableText
                                          value={column[selectedTextField[index]] ?? []}
                                          components={richTextComponent}
                                        />
                                      </div>
                                    </div>
                                  </div>
                                ))}
                            </div>
                          </div>
                        </Fragment>
                      )}
                    </div>
                  </div>

                  {item?.showSideBanner && (
                    <div className="hidden absolute z-10 justify-center items-center rotate-45 lg:flex top-[5.75rem] -right-[6rem] bg-[#6FD19A] h-[4.25rem] px-[5rem] drop-shadow-[0_5.353px_18.734px_rgba(0,0,0,0.25)]">
                      <div
                        className="absolute top-full w-0 h-0 left-[.625rem]"
                        style={{
                          borderLeft: ".675rem solid #427762",
                          borderRight: ".675rem solid transparent",
                          borderTop: ".675rem solid #427762",
                          borderBottom: ".675rem solid transparent",
                        }}
                      />
                      <div
                        className="absolute top-full w-0 h-0 right-[.75rem]"
                        style={{
                          borderRight: ".75rem solid #427762",
                          borderLeft: ".75rem solid transparent",
                          borderTop: ".75rem solid #427762",
                          borderBottom: ".75rem solid transparent",
                        }}
                      />
                      <p className="flex items-center mb-0 tracking-tight leading-none whitespace-nowrap text-[#103037] gap-[.75rem]">
                        <span className="font-semibold text-[1.75rem]">
                          {item.showSideBannerValueText ?? "10k just $1"}
                        </span>
                        <span className="opacity-70 text-[1.375rem]">
                          {item.showSideBannerDescText ?? "Start-Angebot"}
                        </span>
                      </p>
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
